<script context="module">
  export const i18n = {
    sv: {
      Submit: 'Skicka'
    }
  }
</script>

<script>
  import { text, register } from '$lib/text.js'
  import Textarea from '$lib/Textarea.svelte'
  import Button from '$lib/Button.svelte'
  import Select from '$lib/Select.svelte'
  import Input from '$lib/Input.svelte'

  register(i18n)

  /** @type {{ type: string, label: string, name: string, value?: string|number|boolean, placeholder?: string, options?: string[], required?: boolean, honeypot?: boolean }[]}*/
  export let fields = []

  /** @type {'SUBMITTING'|'SUCCESS'|'ERROR'|'IDLE'} */
  export let state = 'IDLE'

  export let buttonText = $text`Submit`
</script>

<div class="contact-form">
  <div class="body">
    <slot />
  </div>
  <fieldset class="form" disabled={state === 'SUBMITTING'}>
    {#each fields as field, index}
      {#if field.options?.length}
        <Select
          name={field.name}
          label={field.label}
          options={field.options.map((value) => ({ value }))}
          required={field.required} />
      {:else if field.type === 'textarea'}
        <Textarea
          name={field.name}
          label={field.label}
          rows={5}
          required={field.required}
          placeholder={field.placeholder}
          value={field.value ? String(field.value) : ''} />
      {:else if field.type === 'checkbox'}
        {@const id = `checkbox-${index}-${field.name}`}
        <label class="checkbox" for={id}>
          <input
            {id}
            class="field"
            type="checkbox"
            name={field.name}
            checked={Boolean(field.value)} />
          {field.label}
        </label>
      {:else if field.honeypot}
        <label class="gotcha">
          {field.label}
          <input
            name={field.name}
            value={field.value}
            tabindex="-1"
            autocomplete="off" />
        </label>
      {:else}
        <Input
          type={field.type}
          name={field.name}
          honeypot={field.honeypot}
          label={field.label}
          value={String(field.value || '')}
          placeholder={field.placeholder}
          required={field.required} />
      {/if}
    {/each}
    {#if state === 'SUCCESS'}
      <div class="message success">
        <slot name="success" />
      </div>
    {:else if state === 'ERROR'}
      <div class="message error">
        {$text`Something went wrong. Please try again or use our email address instead,`}
        <a class="u-link" href="mailto:team@allaboard.ed">team@allaboard.eu</a>
      </div>
    {/if}
    {#if state !== 'SUCCESS'}
      <div class="button">
        <Button type="submit">
          {buttonText}
        </Button>
      </div>
    {/if}
  </fieldset>
</div>

<style>
  .contact-form {
    display: grid;
    gap: 1.5rem 4rem;
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    background: rgb(var(--rgb-blue));
    padding: 3rem min(max(var(--document-margin), 1.5rem), 2rem) 2rem;
    border-radius: var(--border-radius-xl);
    color: rgb(var(--rgb-white));
    box-shadow: var(--box-shadow-lg);
  }

  .gotcha {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    overflow: hidden;
    opacity: 0;
  }

  .button {
    align-self: flex-end;
  }

  @media (min-width: 1000px) {
    .contact-form {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (min-width: 1300px) {
    .contact-form {
      justify-content: space-between;
      grid-template-columns: 36rem max(42%, 20rem);
    }

    .form {
      padding: 2.75rem 3rem 2.25rem;
    }
  }

  /* Checkbox */

  .checkbox {
    display: flex;
    align-items: start;
    gap: 0.75rem;
    user-select: none;
    text-wrap: balance;
  }

  .checkbox .field {
    flex: 0 0 auto;
    width: 1.25rem;
    height: 1.25rem;
    padding: 0;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    cursor: pointer;
    appearance: none;
    background: var(--background);
    box-shadow: var(--box-shadow-sm);
    border-radius: var(--border-radius-xs);
    transition: var(--box-shadow-transition);
  }

  .checkbox .field:checked {
    background-image: url('$lib/assets/checkmark.svg');
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: 0 2px;
  }

  .checkbox .field:focus {
    border-color: rgba(var(--rgb-gray), 0.5);
    box-shadow: var(--box-shadow-sm-hover);
  }

  .checkbox:hover .field {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .checkbox:active .field {
    box-shadow: var(--box-shadow-sm-active);
  }

  /* Replies */

  .message {
    --current-font-size: 1rem;
    --current-color: rgb(var(--rgb-white));

    padding: 0.75rem;
    border-radius: var(--border-radius-sm);
    border: 1px solid rgba(var(--rgb-gray), 0.45);
  }

  .success {
    background: rgba(var(--rgb-bright-green), 0.35);
    border-color: rgb(var(--rgb-bright-green));
  }

  .error {
    background: rgba(var(--rgb-red), 0.15);
    border-color: rgb(var(--rgb-red));
  }
</style>
